import { useCallback, useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import type { ActivityAnalyticsSnapshot } from '../../server/activity-analytics.js';
import { Alert, Badge, Button, PanelHeader, Screen, TextField } from '../ui/kit/index.js';

const TOKEN_STORAGE_KEY = 'cards:activity-admin-token:v1';
const ENDPOINT = '/api/admin/activity-notifications';
const ANALYTICS_ENDPOINT = '/api/admin/activity-analytics';

interface NotificationSettings {
  enabled: boolean;
  registrationEnabled: boolean;
  searchEnabled: boolean;
  maintenanceEnabled: boolean;
  assistantEnabled: boolean;
  notificationsConfigured: boolean;
}

type EditableSettings = Omit<NotificationSettings, 'notificationsConfigured'>;

function editableSettings(settings: NotificationSettings): EditableSettings {
  return {
    enabled: settings.enabled,
    registrationEnabled: settings.registrationEnabled,
    searchEnabled: settings.searchEnabled,
    maintenanceEnabled: settings.maintenanceEnabled,
    assistantEnabled: settings.assistantEnabled,
  };
}

interface FlagDefinition {
  readonly key: keyof EditableSettings;
  readonly title: string;
  readonly description: string;
}

const FLAGS: readonly FlagDefinition[] = [
  {
    key: 'enabled',
    title: 'Все уведомления',
    description: 'Главный выключатель: новые сообщения в Telegram не отправляются.',
  },
  {
    key: 'registrationEnabled',
    title: 'Регистрации',
    description: 'Пинг после того, как новый игрок укажет публичный ник.',
  },
  {
    key: 'searchEnabled',
    title: 'Поиск соперника',
    description: 'Пинг, если игрок ждёт соперника больше секунды.',
  },
  {
    key: 'maintenanceEnabled',
    title: 'Технические работы',
    description: 'Вместо игры все посетители, кроме оператора, увидят экран технических работ.',
  },
  {
    key: 'assistantEnabled',
    title: 'AI-помощник',
    description: 'Отключает запросы к модели и показывает игрокам режим технического обслуживания AI.',
  },
];

function storedToken(): string {
  try {
    return window.sessionStorage.getItem(TOKEN_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function rememberToken(token: string): void {
  try {
    window.sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // The current tab still keeps the token in component state.
  }
}

function forgetToken(): void {
  try {
    window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // Nothing else to clean up.
  }
}

async function requestSettings(token: string, method: 'GET' | 'PUT' = 'GET', body?: EditableSettings): Promise<NotificationSettings> {
  const response = await fetch(ENDPOINT, {
    method,
    headers: {
      authorization: `Bearer ${token}`,
      ...(body === undefined ? {} : {
        'content-type': 'application/json',
      }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (response.status === 401) throw new Error('Неверный токен доступа.');
  if (!response.ok) throw new Error('Не удалось обновить настройки.');
  const value = await response.json() as Partial<NotificationSettings>;
  if (typeof value.enabled !== 'boolean'
    || typeof value.registrationEnabled !== 'boolean'
    || typeof value.searchEnabled !== 'boolean'
    || typeof value.maintenanceEnabled !== 'boolean'
    || typeof value.assistantEnabled !== 'boolean'
    || typeof value.notificationsConfigured !== 'boolean') {
    throw new Error('Сервер вернул некорректные настройки.');
  }
  return {
    enabled: value.enabled,
    registrationEnabled: value.registrationEnabled,
    searchEnabled: value.searchEnabled,
    maintenanceEnabled: value.maintenanceEnabled,
    assistantEnabled: value.assistantEnabled,
    notificationsConfigured: value.notificationsConfigured,
  };
}

async function requestAnalytics(token: string): Promise<ActivityAnalyticsSnapshot> {
  const response = await fetch(ANALYTICS_ENDPOINT, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Аналитика пока недоступна.');
  return await response.json() as ActivityAnalyticsSnapshot;
}

function DailyChart({
  title,
  values,
}: {
  title: string;
  values: readonly { day: string; value: number }[];
}): ReactElement {
  const peak = Math.max(1, ...values.map((point) => point.value));
  const firstDay = values[0]?.day.slice(5).replace('-', '.') ?? '';
  const lastDay = values.at(-1)?.day.slice(5).replace('-', '.') ?? '';
  return (
    <figure className="activity-admin-chart">
      <figcaption>{title}</figcaption>
      <div className="activity-admin-chart-bars" role="img" aria-label={`${title}: последние 30 дней`}>
        {values.map((point) => (
          <div className="activity-admin-chart-column" key={point.day} title={`${point.day}: ${point.value}`}>
            <span style={{ height: `${Math.max(3, Math.round((point.value / peak) * 100))}%` }} />
          </div>
        ))}
      </div>
      <div className="activity-admin-chart-axis"><span>{firstDay}</span><span>{lastDay}</span></div>
    </figure>
  );
}

function FunnelChart({ funnel }: { funnel: ActivityAnalyticsSnapshot['funnel'] }): ReactElement {
  const points = [
    ['Аккаунты', funnel.accounts],
    ['Выбрали ник', funnel.activeProfiles],
    ['Получили стартовую колоду', funnel.starterDecks],
    ['Запустили обучение', funnel.trainingStarted],
    ['Завершили тур лобби', funnel.lobbyTourCompleted],
  ] as const;
  const peak = Math.max(1, ...points.map(([, value]) => value));
  return (
    <section className="activity-admin-funnel" aria-labelledby="activity-admin-funnel-title">
      <h2 id="activity-admin-funnel-title">Воронка онбординга</h2>
      {points.map(([label, value]) => (
        <div className="activity-admin-funnel-row" key={label}>
          <span>{label}</span>
          <div aria-label={`${label}: ${value}`}><i style={{ width: `${Math.round((value / peak) * 100)}%` }} /></div>
          <strong>{value}</strong>
        </div>
      ))}
    </section>
  );
}

export function ActivityNotificationsAdmin(): ReactElement {
  const [token, setToken] = useState('');
  const [draftToken, setDraftToken] = useState(storedToken);
  const [settings, setSettings] = useState<NotificationSettings | undefined>();
  const [loading, setLoading] = useState(storedToken() !== '');
  const [savingKey, setSavingKey] = useState<keyof EditableSettings | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [analytics, setAnalytics] = useState<ActivityAnalyticsSnapshot | undefined>();
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | undefined>();

  const refreshAnalytics = useCallback(async (candidate: string): Promise<void> => {
    if (candidate === '') return;
    setAnalyticsLoading(true);
    setAnalyticsError(undefined);
    try {
      setAnalytics(await requestAnalytics(candidate));
    } catch (reason) {
      setAnalytics(undefined);
      setAnalyticsError(reason instanceof Error ? reason.message : 'Аналитика пока недоступна.');
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const load = useCallback(async (candidate: string): Promise<void> => {
    setLoading(true);
    setError(undefined);
    try {
      const received = await requestSettings(candidate);
      setToken(candidate);
      rememberToken(candidate);
      setSettings(received);
      void refreshAnalytics(candidate);
    } catch (reason) {
      forgetToken();
      setToken('');
      setSettings(undefined);
      setError(reason instanceof Error ? reason.message : 'Не удалось войти.');
    } finally {
      setLoading(false);
    }
  }, [refreshAnalytics]);

  useEffect(() => {
    const initialToken = storedToken();
    if (initialToken !== '') void load(initialToken);
  }, [load]);

  const toggle = useCallback(async (key: keyof EditableSettings): Promise<void> => {
    if (settings === undefined || savingKey !== undefined) return;
    const next = { ...editableSettings(settings), [key]: !settings[key] };
    setSavingKey(key);
    setError(undefined);
    try {
      setSettings(await requestSettings(token, 'PUT', next));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось обновить настройки.');
    } finally {
      setSavingKey(undefined);
    }
  }, [savingKey, settings, token]);

  const logout = (): void => {
    forgetToken();
    setToken('');
    setDraftToken('');
    setSettings(undefined);
    setAnalytics(undefined);
    setAnalyticsError(undefined);
    setError(undefined);
  };

  return (
    <Screen as="main" layout="centered" height="dynamic" className="activity-admin-screen" data-testid="activity-admin">
      <section className="activity-admin-panel" aria-labelledby="activity-admin-title">
        <PanelHeader
          kicker="ОПЕРАТОР"
          title="Уведомления"
          headingId="activity-admin-title"
          actions={settings === undefined ? undefined : (
            <Button variant="ghost" size="sm" onClick={logout}>Выйти</Button>
          )}
        />
        {settings === undefined ? (
          <form className="activity-admin-login" onSubmit={(event) => {
            event.preventDefault();
            if (draftToken !== '') void load(draftToken);
          }}>
            <label htmlFor="activity-admin-token">Токен доступа</label>
            <TextField
              id="activity-admin-token"
              type="password"
              autoComplete="current-password"
              value={draftToken}
              disabled={loading}
              onChange={(event) => setDraftToken(event.currentTarget.value)}
            />
            <Button type="submit" disabled={loading || draftToken === ''}>
              {loading ? 'Проверяем…' : 'Войти'}
            </Button>
          </form>
        ) : (
          <div className="activity-admin-flags" aria-label="Флаги уведомлений">
            {FLAGS.map((flag) => {
              const active = settings[flag.key];
              const saving = savingKey === flag.key;
              const notificationFlag = ['enabled', 'registrationEnabled', 'searchEnabled'].includes(flag.key);
              return (
                <section className="activity-admin-flag" key={flag.key}>
                  <div>
                    <h2>{flag.title}</h2>
                    <p>{flag.description}</p>
                  </div>
                  <Button
                    className="activity-admin-switch"
                    variant={active ? 'primary' : 'secondary'}
                    size="sm"
                    aria-pressed={active}
                    disabled={savingKey !== undefined || (notificationFlag && !settings.notificationsConfigured)}
                    onClick={() => { void toggle(flag.key); }}
                  >
                    {saving ? '…' : active ? 'Вкл' : 'Выкл'}
                  </Button>
                </section>
              );
            })}
          </div>
        )}
        {settings === undefined ? null : (
          <Badge tone={settings.enabled ? 'success' : 'warning'}>
            {settings.enabled ? 'Отправка включена' : 'Отправка выключена'}
          </Badge>
        )}
        {settings !== undefined && !settings.notificationsConfigured ? (
          <Alert tone="warning">Telegram не настроен: флаги уведомлений недоступны, остальные переключатели работают.</Alert>
        ) : null}
        {settings === undefined ? null : (
          <section className="activity-admin-analytics" aria-labelledby="activity-admin-analytics-title">
            <PanelHeader
              title="Аналитика"
              headingLevel={2}
              headingId="activity-admin-analytics-title"
              actions={<Button variant="ghost" size="sm" disabled={analyticsLoading} onClick={() => { void refreshAnalytics(token); }}>
                {analyticsLoading ? 'Обновляем…' : 'Обновить'}
              </Button>}
            />
            {analytics === undefined ? (
              analyticsError === undefined ? <div role="status">Загружаем данные…</div> : <Alert tone="warning">{analyticsError}</Alert>
            ) : (
              <>
                <div className="activity-admin-charts">
                  <DailyChart title="Новые аккаунты — 30 дней" values={analytics.days.map((point) => ({ day: point.day, value: point.accounts }))} />
                  <DailyChart title="Созданные колоды — 30 дней" values={analytics.days.map((point) => ({ day: point.day, value: point.decks }))} />
                  <DailyChart title="Пройденные челленджи — 30 дней" values={analytics.days.map((point) => ({ day: point.day, value: point.challengeCompletions }))} />
                </div>
                <FunnelChart funnel={analytics.funnel} />
                <section className="activity-admin-ai" aria-labelledby="activity-admin-ai-title">
                  <h2 id="activity-admin-ai-title">Расходы AI</h2>
                  <div><span>Запросов к модели</span><strong>{analytics.assistant.requests}</strong></div>
                  <div><span>Входных токенов</span><strong>{analytics.assistant.inputTokens.toLocaleString('ru-RU')}</strong></div>
                  <div><span>Выходных токенов</span><strong>{analytics.assistant.outputTokens.toLocaleString('ru-RU')}</strong></div>
                  <div><span>Оценка расходов</span><strong>${analytics.assistant.estimatedCostUsd.toFixed(4)}</strong></div>
                  <p>Оценка по цене модели на момент запроса; данные не содержат текстов диалогов.</p>
                </section>
              </>
            )}
          </section>
        )}
        {error === undefined ? null : <Alert tone="error">{error}</Alert>}
      </section>
    </Screen>
  );
}
